import type { CalEventType } from "@/lib/cal/client";

/**
 * Resolves the parent event for MANAGED responses.
 * Falls back to the first item when parent linkage is ambiguous.
 */
export function resolveManagedParentEvent(
  eventType: CalEventType | CalEventType[],
): CalEventType | undefined {
  if (!Array.isArray(eventType)) {
    return eventType;
  }
  if (eventType.length === 0) {
    return undefined;
  }

  const rootCandidate = eventType.find((candidate) => candidate.parentEventTypeId == null);
  if (rootCandidate) {
    return rootCandidate;
  }

  const referencedParentIds = Array.from(
    new Set(
      eventType
        .map((candidate) => candidate.parentEventTypeId)
        .filter((parentId): parentId is number => typeof parentId === "number"),
    ),
  );
  if (referencedParentIds.length === 1) {
    return eventType.find((candidate) => candidate.id === referencedParentIds[0]) ?? eventType[0];
  }

  return eventType[0];
}

/**
 * Resolves child events for a managed parent.
 * Uses explicit parent links when present, otherwise falls back to non-parent items.
 */
export function resolveManagedChildEvents(
  eventType: CalEventType | CalEventType[],
  parentEventId: number,
): CalEventType[] {
  if (!Array.isArray(eventType)) {
    return [];
  }

  const linkedChildren = eventType.filter(
    (candidate) => candidate.parentEventTypeId === parentEventId,
  );
  if (linkedChildren.length > 0) {
    return linkedChildren;
  }

  return eventType.filter((candidate) => candidate.id !== parentEventId);
}

/**
 * Counts managed child events for logging/observability.
 */
export function countManagedChildEvents(
  eventType: CalEventType | CalEventType[],
  parentEventId: number,
): number {
  return resolveManagedChildEvents(eventType, parentEventId).length;
}
